import { DocumentSnapshot, logger, Timestamp } from "../firestoreImports"
import { UserPriorityLevel } from "./user"
import { z } from "zod"
import moment from "moment"
import { computeReservationStatus, IReservation, Reservation, reservationBaseShape } from "./reservation"

const visitorReservationSchema = z
    .object({
        ...reservationBaseShape,
        invitedBy: z.string().nullish(),
        confirmationEmails: z.array(z.string()).nullish().default([]),
        sendEmail: z.boolean().default(false)
    })
    .transform(data => ({
        ...data,
        status: computeReservationStatus(data.abused ?? false, data.canceled ?? false, data.confirmed, data.status)
    }))

export interface IVisitorReservation extends IReservation {
    invitedBy?: string
    confirmationEmails?: string[]
    sendEmail?: boolean
}

export interface VisitorReservation extends IVisitorReservation {}
export class VisitorReservation extends Reservation {
    constructor(params: IVisitorReservation) {
        super(params)
        this.invitedBy = params.invitedBy
        this.confirmationEmails = params.confirmationEmails
        this.sendEmail = params.sendEmail
    }

    static fromData(doc: DocumentSnapshot): VisitorReservation | null {
        const raw = doc.data()
        if (!raw) {
            logger.warn(`Cannot create VisitorReservation ${doc.id} from snapshot: missing data`)
            return null
        }

        try {
            const parsed = visitorReservationSchema.parse(raw)
            return new VisitorReservation({
                id: doc.id,
                userId: parsed.userId,
                startTime: parsed.startTime.toDate(),
                endTime: parsed.endTime.toDate(),
                confirmed: parsed.confirmed,
                canceled: parsed.canceled ?? false,
                abused: parsed.abused ?? false,
                dateCanceled: parsed.dateCanceled?.toDate() ?? undefined,
                entryDates: parsed.entryDates?.map((t: Timestamp) => t.toDate()) ?? [],
                exitDates: parsed.exitDates?.map((t: Timestamp) => t.toDate()) ?? [],
                parkingSpot: parsed.parkingSpot ?? undefined,
                parkingSpotId: parsed.parkingSpotId ?? undefined,
                priority: parsed.priority ?? UserPriorityLevel.Basic,
                carId: parsed.carId ?? undefined,
                rideId: parsed.rideId ?? undefined,
                timestampCreated: parsed.timestampCreated?.toDate() ?? undefined,
                gateReservationsIds: parsed.gateReservationsIds ?? undefined,
                status: parsed.status,
                invitedBy: parsed.invitedBy ?? undefined,
                confirmationEmails: parsed.confirmationEmails ?? undefined,
                sendEmail: parsed.sendEmail ?? false
            })
        } catch (error) {
            logger.error(`Cannot create VisitorReservation ${doc.id} from snapshot:`, error)
            return null
        }
    }

    override asData(): Record<string, unknown> {
        return visitorReservationSchema.parse({
            userId: this.userId,
            startTime: Timestamp.fromDate(this.startTime),
            endTime: Timestamp.fromDate(this.endTime),
            confirmed: this.confirmed ?? null,
            canceled: this.canceled ?? false,
            abused: this.abused ?? false,
            dateCanceled: this.dateCanceled ? Timestamp.fromDate(this.dateCanceled) : undefined,
            entryDates: this.entryDates?.map((d: Date) => Timestamp.fromDate(d)),
            exitDates: this.exitDates?.map((d: Date) => Timestamp.fromDate(d)),
            parkingSpot: this.parkingSpot,
            parkingSpotId: this.parkingSpotId,
            priority: this.priority ?? UserPriorityLevel.Basic,
            carId: this.carId,
            rideId: this.rideId,
            timestampCreated: this.timestampCreated ?? Timestamp.now(),
            gateReservationsIds: this.gateReservationsIds,
            status: this.status,
            invitedBy: this.invitedBy,
            confirmationEmails: this.confirmationEmails,
            sendEmail: this.sendEmail ?? false,
            // Legacy date, Garage app needs this to work (as of 22. 12. 2025)
            date: Timestamp.fromDate(moment.tz(this.startTime, "Europe/Prague").set({ hour: 10, minute: 0, second: 0, millisecond: 0 }).toDate())
        })
    }
}
